import { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/ui/NotificationsBell.module.css";
import { ReactComponent as CampanaIcon } from "../../assets/icons/notification.svg";
import { notificationsService } from "../../services/notifications.service";

function formatRelativeDate(dateString) {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;

  const minutes = Math.floor(diffMs / 1000 / 60);
  const hours = Math.floor(diffMs / 1000 / 60 / 60);
  const days = Math.floor(diffMs / 1000 / 60 / 60 / 24);

  if (minutes < 1) return "Hace un momento";
  if (minutes < 60) return `Hace ${minutes} min`;
  if (hours < 24) return `Hace ${hours} hora${hours > 1 ? "s" : ""}`;
  return `Hace ${days} día${days > 1 ? "s" : ""}`;
}

function getBadgeLabel(count) {
  if (!count || count < 1) return "";
  if (count > 99) return "99+";
  return String(count);
}

const NotificationsBell = ({
  notifications = [],
  unreadCount,
  onNotificationClick,
  onDropdownOpen,
  onOpenAll,
}) => {
  const [open, setOpen] = useState(false);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [localNotifications, setLocalNotifications] = useState(notifications);
  const [localUnreadCount, setLocalUnreadCount] = useState(unreadCount);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setLocalNotifications(notifications);
  }, [notifications]);

  useEffect(() => {
    setLocalUnreadCount(unreadCount);
  }, [unreadCount]);

  const unreadNotifications = useMemo(
    () => localNotifications.filter((notification) => !notification.is_read),
    [localNotifications],
  );

  const computedUnreadCount = useMemo(() => {
    if (typeof localUnreadCount === "number") return localUnreadCount;
    return unreadNotifications.length;
  }, [unreadNotifications, localUnreadCount]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);

    if (next && onDropdownOpen) {
      onDropdownOpen();
    }
  };

  const handleMarkAllAsRead = async () => {
    if (computedUnreadCount < 2 || markingAllAsRead) return;

    try {
      setMarkingAllAsRead(true);

      const result = await notificationsService.markAllRead();

      setLocalNotifications((prev) =>
        prev.map((notification) => ({
          ...notification,
          is_read: true,
        })),
      );

    setLocalUnreadCount(result.unread_count);
    } catch (error) {
      console.error("Error marking all notifications as read");
      console.error("data:", error?.response?.data);
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.iconButton}
        aria-label="Notificaciones"
        onClick={handleToggle}
      >
        <CampanaIcon />

        {computedUnreadCount > 0 ? (
          <>
            <span className={styles.dot} />
            <span className={styles.counter}>
              {getBadgeLabel(computedUnreadCount)}
            </span>
          </>
        ) : null}
      </button>

      {open ? (
        <div className={styles.dropdown}>
          <div className={styles.header}>
            <span className={styles.title}>Notificaciones</span>
            {computedUnreadCount > 0 ? (
              <span className={styles.headerCount}>
                {computedUnreadCount} nuevas
              </span>
            ) : null}
          </div>

          <div className={styles.list}>
            {unreadNotifications.length ? (
              <>
                {unreadNotifications.map((notification) => (
                  <button
                    key={notification.id}
                    type="button"
                    className={styles.item}
                    onClick={() => onNotificationClick?.(notification)}
                  >
                    <div className={styles.itemContent}>
                      <div className={styles.itemTopRow}>
                        <div className={styles.itemTitle}>
                          {notification.title}
                        </div>

                        {notification.account_name ? (
                          <span className={styles.pill}>
                            {notification.account_name}
                          </span>
                        ) : null}
                      </div>

                      {notification.message ? (
                        <div className={styles.itemMessage}>
                          {notification.message}
                        </div>
                      ) : null}

                      <div className={styles.itemTime}>
                        {formatRelativeDate(notification.created_at)}
                      </div>
                    </div>

                    <span className={styles.itemUnreadDot} />
                  </button>
                ))}

                {computedUnreadCount > 1 ? (
                    <button
                      type="button"
                      className={`${styles.item} ${styles.markAllItem}`}
                      onClick={handleMarkAllAsRead}
                      disabled={markingAllAsRead}
                    >
                      <div className={styles.itemContent}>
                        <div className={styles.markAllText}>
                          {markingAllAsRead
                            ? "Marcando como leídas..."
                            : "Marcar todo como leído"}
                        </div>
                      </div>
                    </button>
                ) : null}
              </>
            ) : (
              <div className={styles.empty}>No tenés notificaciones sin leer.</div>
            )}
          </div>

          {onOpenAll ? (
            <button
              type="button"
              className={styles.footerLink}
              onClick={() => {
                setOpen(false);
                onOpenAll();
              }}
            >
              Ver todas las notificaciones
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default NotificationsBell;
